import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAgreementApi,
  useAgreementCreateApi,
  useAgreementUpdateApi,
} from '@hero/api-hooks';
import { getObjectId } from '@hero/schema';
import type { Agreement, Id, PartyRole } from '@hero/schema';
import { useAgreementForm } from './useAgreementForm';
import type { AgreementFormValues, UseAgreementFormOptions } from './useAgreementForm';

/**
 * Domain-shaped defaults the manager understands when seeding form
 * state for a fresh agreement (e.g. opening the modal from a Client
 * workspace pre-fills the counterparty side of the bilateral
 * `parties[]` tuple). Lives here — not on the form hook — because
 * mapping these light fields into the full form-values shape is a
 * manager-tier concern; the form hook only speaks RHF.
 *
 * `counterpartyRoles` is optional — when omitted, the manager
 * defaults to `['business_customer']` so the bilateral `parties[]`
 * tuple has a valid role on the counterparty side.
 */
export interface AgreementCreateDefaults {
  counterpartyId?: string;
  counterpartyName?: string;
  counterpartyRoles?: PartyRole[];
}

/**
 * Options for {@link useAgreementEditManager}.
 */
export interface UseAgreementEditManagerOptions {
  /**
   * Agreement id to edit. When set, the manager loads the existing
   * agreement (`useAgreementApi`) and populates the form. When
   * undefined, the manager is in create mode and submits via
   * `useAgreementCreateApi`.
   */
  agreementId?: Id;
  /**
   * Org id of the current user. Used as the org-side party of the
   * bilateral `parties[]` tuple on create. Pulled from
   * `useCurrentUser()` by the consumer (modal) and threaded down
   * here so the manager doesn't reach into auth state directly.
   */
  orgId?: string;
  /**
   * Initial counterparty hints for create mode (e.g. opening the
   * modal from a Client workspace pre-fills the counterparty). The
   * manager maps these into the form-values shape — callers don't
   * need to know about the `parties[]` / `Party` shape. Ignored in
   * edit mode (the loaded agreement is the source of truth).
   */
  defaults?: AgreementCreateDefaults;
  /**
   * Fired with the saved Agreement after a successful create or
   * update. Modals typically use this to close themselves and
   * refresh upstream lists.
   */
  onAgreementSaved?: (agreement: Agreement) => void;
}

/**
 * `useAgreementEditManager` — Orchestrates create + edit flows for
 * Agreement.
 *
 * Tier-3 manager hook (per the `add-web-hook` skill): wires the
 * form-state hook ({@link useAgreementForm}) against the API hooks
 * (`useAgreementApi` / `useAgreementCreateApi` /
 * `useAgreementUpdateApi`) so the modal layer can render
 * `<AgreementForm formHook={...} />` plus a Save button without
 * knowing about either side.
 *
 * Mirrors the established `useContactCreateAdapter` /
 * `useSavedLocationCreateAdapter` shape (renamed to "EditManager"
 * per the new naming convention — see `add-web-hook` skill: rename
 * Adapter -> Manager).
 *
 * Edit-mode payload note: `agreementUpdateAPISchema` omits `orgId`
 * + `counterparty` (both immutable post-create). We strip them from
 * the form values before calling `update.mutateAsync` so the
 * backend's strict-shape Zod parse doesn't see unexpected fields.
 */
export function useAgreementEditManager(options: UseAgreementEditManagerOptions) {
  const { agreementId, orgId, defaults, onAgreementSaved } = options;

  const { t } = useTranslation('m-agreement');

  const agreementQuery = useAgreementApi(agreementId);
  const savedAgreement = agreementQuery.data;
  const createApi = useAgreementCreateApi();
  const updateApi = useAgreementUpdateApi();

  // Translate the domain-shaped `defaults` into the RHF-shaped
  // `defaultValues` once, then memoize so `useAgreementForm` doesn't
  // re-seed on every render. The form hook spreads these over its
  // own create-mode defaults (today + 1y, weekly/monthly terms, …).
  //
  // We also pre-fill `title` here when we have a counterparty name —
  // the suggested format is `{client} Contract {year}` in English /
  // any non-Arabic locale, and `عقد {client} {year}` in Arabic (the
  // word "عقد" leads, matching natural Arabic phrasing). The user
  // can overwrite it freely.
  const defaultValues = useMemo<UseAgreementFormOptions['defaultValues']>(() => {
    if (!defaults?.counterpartyId) return undefined;
    return {
      counterparty: {
        _id: getObjectId(defaults.counterpartyId),
        target: 'client',
        roles: defaults.counterpartyRoles ?? ['business_customer'],
        name: defaults.counterpartyName,
      },
      title: defaults.counterpartyName
        ? buildSuggestedTitle(t, defaults.counterpartyName)
        : undefined,
    } as UseAgreementFormOptions['defaultValues'];
  }, [defaults?.counterpartyId, defaults?.counterpartyName, defaults?.counterpartyRoles, t]);

  async function onSubmit(values: AgreementFormValues) {
    try {
      let result: Agreement;
      if (savedAgreement) {
        // Strip immutable fields — `agreementUpdateAPISchema` rejects them.
        const { orgId: _orgId, counterparty: _counterparty, ...patch } = values;
        result = await updateApi.mutateAsync({
          _id: getObjectId(String(savedAgreement._id)),
          ...patch,
        });
      } else {
        result = await createApi.mutateAsync(values);
      }
      onAgreementSaved?.(result);
    } catch (error) {
      // Errors are surfaced upstream by the api-hook layer's
      // toast/log machinery; we swallow here so the modal doesn't
      // unmount on failure (the user can retry).
      console.error('Failed to save agreement:', error);
    }
  }

  const formHook = useAgreementForm({
    orgId,
    defaultValues,
    onSubmit,
  });

  // When the agreementId changes between renders (e.g. switching
  // which agreement is being edited inside a single modal mount),
  // reset the form so we don't show stale values from the previous
  // entity while the new one is loading. Same pattern as
  // `useContactCreateAdapter` / `useSavedLocationCreateAdapter`.
  useEffect(() => {
    if (agreementId != null) {
      formHook.reset();
    }
  }, [agreementId, formHook.reset]);

  // When we have an id and the entity has loaded, populate the
  // form. `setAgreement` also marks `endsAtTouched` so the
  // start-date auto-fill doesn't stomp on the loaded end-date.
  useEffect(() => {
    if (agreementId != null && savedAgreement) {
      formHook.setAgreement(savedAgreement);
    }
  }, [agreementId, savedAgreement, formHook.setAgreement]);

  // Async-default safety net (create mode only).
  //
  // RHF's `defaultValues` are consumed once on `useForm` init. If
  // the caller built `defaults` from an async source (e.g. waiting
  // on a `useClientApi` fetch to resolve before constructing the
  // counterparty object) and the drawer mounted *before* that
  // fetch finished, the form initialized with no counterparty and
  // wouldn't pick up the late-arriving value. The first time you'd
  // see the right counterparty was after a close+reopen.
  //
  // This effect re-seeds the counterparty the moment defaults
  // become available — but only when (a) we're in create mode (the
  // saved agreement is the source of truth in edit mode), and
  // (b) the form's counterparty is still unset (so we never stomp
  // on a user choice when the field isn't locked).
  useEffect(() => {
    if (agreementId != null) return;
    if (!defaults?.counterpartyId) return;
    const currentCounterparty = formHook.form.getValues('counterparty');
    if (!currentCounterparty?._id) {
      formHook.form.setValue(
        'counterparty',
        {
          _id: getObjectId(defaults.counterpartyId),
          target: 'client',
          roles: defaults.counterpartyRoles ?? ['business_customer'],
          name: defaults.counterpartyName,
        },
        // `shouldDirty: false` — this is a hydration, not a user
        // edit. Marking dirty would imply the user has unsaved
        // changes the moment the drawer opens.
        { shouldValidate: true, shouldDirty: false },
      );
    }

    // Title hydration is independent from counterparty hydration:
    // when the counterparty was seeded synchronously but its name
    // only arrived a tick later (e.g. the parent passed
    // `externalCustomerName` first, then enriched with the
    // resolved `pickedClient.name`), we still want the auto-suggest
    // to update — but only if the user hasn't typed their own
    // title yet. We guard on `currentTitle` being empty.
    if (defaults.counterpartyName) {
      const currentTitle = formHook.form.getValues('title');
      if (!currentTitle || currentTitle.trim().length === 0) {
        formHook.form.setValue(
          'title',
          buildSuggestedTitle(t, defaults.counterpartyName),
          { shouldValidate: true, shouldDirty: false },
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    agreementId,
    defaults?.counterpartyId,
    defaults?.counterpartyName,
    defaults?.counterpartyRoles,
  ]);

  const reset = () => {
    createApi.reset();
    updateApi.reset();
    if (savedAgreement) {
      formHook.setAgreement(savedAgreement);
    } else {
      formHook.reset();
    }
  };

  const onSaveClick = () => {
    formHook.form.handleSubmit(formHook.onValidSubmit)();
  };

  return {
    agreementQuery,
    createApi,
    updateApi,
    formHook,
    reset,
    onSaveClick,
    /** Convenience flag — true when the manager is in edit mode. */
    isEdit: agreementId != null,
    /** The loaded agreement (edit mode) or undefined (create mode). */
    savedAgreement,
  };
}

/* -------------------------------------------------------------- */
/* Helpers                                                        */
/* -------------------------------------------------------------- */

/**
 * Build the auto-suggested title for a new agreement.
 *
 * Format is locale-driven via `m-agreement:form.title.auto_suggest`:
 *
 *   English / other locales → `{{client}} Contract {{year}}`
 *                              (e.g. "Acme Logistics Contract 2026")
 *   Arabic                  → `عقد {{client}} {{year}}`
 *                              (e.g. "عقد شركة الأنماء 2026")
 *
 * The leading-"عقد" placement in Arabic matches natural phrasing
 * (head noun first); English keeps the company name first. The
 * year is the *current* calendar year on title-build, not the
 * agreement's start year, because (a) most agreements are signed
 * within the same year they're entered and (b) we don't want the
 * suggestion to flicker if the user later edits the start date.
 */
function buildSuggestedTitle(
  t: ReturnType<typeof useTranslation>['t'],
  clientName: string,
): string {
  return t('form.title.auto_suggest', {
    client: clientName,
    year: new Date().getFullYear(),
  });
}
